const toRadians = (deg) => (deg * Math.PI) / 180;

const withCtx = (canvas, ctxUser) => {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctxUser(ctx);
};

const transformYPixel = (canvas, yVal) => canvas.height - yVal;

export const toCanvasCoords = (canvas, options, point, noTransform) => {
    const { left, right, top, bottom } = options.padding;
    const { startX, endX, startY, endY } = options.coordinates;
    const transformationX = options.axes.transformationX;

    const transformedStartX = noTransform ? startX : transformationX(startX);
    const transformedEndX = noTransform ? endX : transformationX(endX);

    const drawableWidth = canvas.width - left - right;
    const drawableHeight = canvas.height - top - bottom;

    const scaleX = drawableWidth / (transformedEndX - transformedStartX);
    const scaleY = drawableHeight / (endY - startY);

    const x = left + (point[0] - transformedStartX) * scaleX;
    const y = canvas.height - (bottom + (point[1] - startY) * scaleY);

    return [x, y];
};

export const decodeRpfString = (profilePointArrayString) => {
    const points = [];
    const profilePointBinary = atob(profilePointArrayString);

    for (let i = 0; i < profilePointBinary.length; i += 4) {
        const data = profilePointBinary.slice(i, i + 4);

        const x = (data[1].charCodeAt(0) << 8) | data[0].charCodeAt(0);
        const y = (data[3].charCodeAt(0) << 8) | data[2].charCodeAt(0);

        const adjusted_x = x / 200;
        const adjusted_y = y / 100;

        points.push([adjusted_x, adjusted_y]);
    }
    return points;
};

export const drawBounds = (canvas, options) => {
    const { left, right, top, bottom } = options.padding;
    const width = canvas.width;

    withCtx(canvas, (ctx) => {
        ctx.strokeStyle = "#aaa";
        ctx.beginPath();
        ctx.moveTo(left, transformYPixel(canvas, bottom));
        ctx.lineTo(width - right, transformYPixel(canvas, bottom));
        ctx.stroke();

        ctx.strokeStyle = "#aaa";
        ctx.beginPath();
        ctx.moveTo(left, transformYPixel(canvas, bottom));
        ctx.lineTo(left, top);
        ctx.stroke();
    });
};

const TICK_HEIGHT = 6;

export const drawTicks = (canvas, options) => {
    const { startX, endX, startY, endY } = options.coordinates;
    const step = options.axes.step;

    withCtx(canvas, (ctx) => {
        for (let i = startX; i <= endX; i += step) {
            const pos = toCanvasCoords(canvas, options, [i, startY]);

            ctx.strokeStyle = "#aaa";
            ctx.beginPath();

            ctx.moveTo(pos[0], pos[1]);
            ctx.lineTo(pos[0], pos[1] + TICK_HEIGHT);
            ctx.fillStyle = "white";
            ctx.font = "12px arial";
            const textWidth = ctx.measureText(i.toString()).width;
            ctx.fillText(i.toString(), pos[0] - textWidth / 2, pos[1] + TICK_HEIGHT + 14);
            ctx.stroke();
        }

        for (let i = startY; i <= endY; i += step) {
            const pos = toCanvasCoords(canvas, options, [startX, i], true);

            ctx.strokeStyle = "#aaa";
            ctx.beginPath();

            ctx.moveTo(pos[0], pos[1]);
            ctx.lineTo(pos[0] - TICK_HEIGHT, pos[1]);
            ctx.fillStyle = "white";
            ctx.font = "12px arial";
            const textWidth = ctx.measureText(i.toString()).width;

            ctx.fillText(i.toString(), pos[0] - TICK_HEIGHT - textWidth - 6, pos[1] + 4);
            ctx.stroke();
        }
    });
};

export const drawPoint = (canvas, options, point) => {
    const [x, y] = toCanvasCoords(canvas, options, point);

    withCtx(canvas, (ctx) => {
        ctx.strokeStyle = "white";
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, 2 * Math.PI);
        ctx.fillStyle = "white";
        ctx.stroke();
    });
};

export const drawPoints = (canvas, canvasOptions, points) => {
    withCtx(canvas, (ctx) => {
        for (const point of points) {
            const [x, y] = toCanvasCoords(canvas, canvasOptions, point);
            ctx.strokeStyle = "white";
            ctx.beginPath();
            ctx.arc(x, y, 2, 0, 2 * Math.PI);
            ctx.fillStyle = "white";
            ctx.stroke();
        }
    });
};

export const drawIdealProfile = (canvas, canvasOptions, idealPointShapes, profileTransform) => {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { dx, dy, angle } = profileTransform;

    const cosA = Math.cos(angle);
    const sinA = Math.sin(angle);

    const { left, right } = canvasOptions.padding;
    const { startX, endX } = canvasOptions.coordinates;

    const drawableWidth = canvas.width - left - right;
    const scaleX = drawableWidth / (endX - startX);

    ctx.strokeStyle = "white";

    const flippedX = canvasOptions.axes.transformationX(startX) > canvasOptions.axes.transformationX(endX);

    const norm = flippedX ? Math.PI : 2 * Math.PI;

    for (const arc of idealPointShapes.arcs) {
        const { center, radius, start_angle, end_angle } = arc;
        const [x, y] = center;
        const cx = x * cosA - y * sinA + dx;
        const cy = x * sinA + y * cosA + dy;
        const _centre = toCanvasCoords(canvas, canvasOptions, [cx, cy]);

        const startAngle = flippedX ? norm + toRadians(end_angle) + angle : norm - (toRadians(start_angle) + angle);
        const endAngle = flippedX ? norm + toRadians(start_angle) + angle : norm - (toRadians(end_angle) + angle);

        ctx.strokeStyle = "#aaa";
        ctx.beginPath();
        ctx.arc(_centre[0], _centre[1], radius * scaleX, startAngle, endAngle, true);
        ctx.stroke();
    }

    for (const line of idealPointShapes.lines) {
        const x1r = line.x1 * cosA - line.y1 * sinA + dx;
        const y1r = line.x1 * sinA + line.y1 * cosA + dy;
        const x2r = line.x2 * cosA - line.y2 * sinA + dx;
        const y2r = line.x2 * sinA + line.y2 * cosA + dy;

        const p1 = toCanvasCoords(canvas, canvasOptions, [x1r, y1r]);
        const p2 = toCanvasCoords(canvas, canvasOptions, [x2r, y2r]);

        ctx.beginPath();
        ctx.moveTo(p1[0], p1[1]);
        ctx.lineTo(p2[0], p2[1]);
        ctx.stroke();
    }
};
